import { Box } from "@rebass/grid";
import { EIntakeStep } from "@rentiohq/shared-frontend/dist/types/application.types";
import { IUser } from "@rentiohq/shared-frontend/dist/types/auth.types";
import { IBroker } from "@rentiohq/shared-frontend/dist/types/broker.types";
import { ELocale } from "@rentiohq/shared-frontend/dist/types/i18n.types";
import { IProperty } from "@rentiohq/shared-frontend/dist/types/property.types";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import { ESpacings, MultiStepForm } from "@rentiohq/web-shared/dist/components";
import * as systemSelectors from "@rentiohq/web-shared/dist/redux/system/system.selectors";
import { useSelector } from "react-redux";
import { IRootStore } from "redux/reducers";
import * as CONFIG from "scenes/Settings/scenes/TenantProfileNotificationPreferences/TenantProfileNotificationPreferences.config";
import propertyApplicationNotEligible from "../../../../../../forms/propertyApplicationNotEligible";
import { getTenantFeedbackTextPerApplication } from "../../../../../../forms/propertyApplicationNotEligible/schema.propertyApplicationNotEligible.utils";
import * as t from "../../../../../../services/translationService";
import { IStatusChange } from "../../PropertyDetailApplicationsContainer";
import { ICustomFeedbackParams } from "./customFeedbackModal.types";
import { IPreference, preferences } from "./customFeedbackModal.utils";

interface IProps {
  property: IProperty;
  user: IUser;
  broker?: IBroker;
  formId: number;
  customFeedbackParams: ICustomFeedbackParams;
  isLoadingInvite: boolean;
  handleChange: (statusChanges: IStatusChange[]) => void;
  setCustomFeedbackParams: (
    customFeedbackParams?: ICustomFeedbackParams,
  ) => void;
}

const getLocalizedMessages = (
  customFeedbackParams: ICustomFeedbackParams,
  notificationPreferences: IPreference[],
) => {
  let localeMessages = {};

  const getPreferenceByKey = (key: string) =>
    notificationPreferences.find(x => x.key === key);

  const statusChange = customFeedbackParams?.statusChanges[0];

  if (statusChange.step === EIntakeStep.visitor) {
    localeMessages = {
      [ELocale.NL]: getPreferenceByKey(CONFIG.PREFERENCE_VISITOR_NL)?.value,
      [ELocale.FR]: getPreferenceByKey(CONFIG.PREFERENCE_VISITOR_FR)?.value,
      [ELocale.EN]: getPreferenceByKey(CONFIG.PREFERENCE_VISITOR_EN)?.value,
    };
  } else if (statusChange.step === EIntakeStep.waitingList) {
    localeMessages = {
      [ELocale.NL]: getPreferenceByKey(CONFIG.PREFERENCE_WAITING_LIST_NL)
        ?.value,
      [ELocale.FR]: getPreferenceByKey(CONFIG.PREFERENCE_WAITING_LIST_FR)
        ?.value,
      [ELocale.EN]: getPreferenceByKey(CONFIG.PREFERENCE_WAITING_LIST_EN)
        ?.value,
    };
  } else if (statusChange.step === EIntakeStep.planAsVisitor) {
    localeMessages = {
      [ELocale.NL]: getPreferenceByKey(CONFIG.PREFERENCE_PLAN_FOR_VISIT_NL)
        ?.value,
      [ELocale.FR]: getPreferenceByKey(CONFIG.PREFERENCE_PLAN_FOR_VISIT_FR)
        ?.value,
      [ELocale.EN]: getPreferenceByKey(CONFIG.PREFERENCE_PLAN_FOR_VISIT_EN)
        ?.value,
    };
  } else if (statusChange.step === EIntakeStep.notEligible) {
    localeMessages = {
      [ELocale.NL]: getPreferenceByKey(CONFIG.PREFERENCE_NOT_ELIGIBLE_NL)
        ?.value,
      [ELocale.FR]: getPreferenceByKey(CONFIG.PREFERENCE_NOT_ELIGIBLE_FR)
        ?.value,
      [ELocale.EN]: getPreferenceByKey(CONFIG.PREFERENCE_NOT_ELIGIBLE_EN)
        ?.value,
    };
  }

  return localeMessages;
};

export const CustomFeedbackModal = ({
  property,
  user,
  broker,
  formId,
  customFeedbackParams,
  isLoadingInvite,
  handleChange,
  setCustomFeedbackParams,
}: IProps) => {
  const notificationPreferences = useSelector((state: IRootStore) =>
    systemSelectors.getPreferences(state, preferences),
  );
  const { statusChanges, applications } = customFeedbackParams;

  // Built once and handed to both the form and the send, so what the broker was
  // shown and what each candidate is sent are worked out from the same inputs.
  const extraData = {
    property,
    applications,
    user,
    broker,
    localeMessages: getLocalizedMessages(
      customFeedbackParams,
      notificationPreferences,
    ),
  };

  return (
    <MultiStepForm
      preventSubmitThroughEnter={true}
      formId={`application-mail-not_eligible-${formId}`}
      schemas={propertyApplicationNotEligible(extraData)}
      asModal={true}
      withAside={false}
      onSuccess={(input: { [field: string]: string }) => {
        // One box per language in the selection, so each candidate is sent the
        // text of their own, edited by the broker or not.
        const textPerApplication = getTenantFeedbackTextPerApplication(
          extraData,
          input,
        );

        handleChange(
          statusChanges.map(statusChange => ({
            ...statusChange,
            tenantFeedbackText: textPerApplication[statusChange.id],
          })),
        );
        setCustomFeedbackParams(undefined);
      }}
      submitLabel={t.system("submit")}
      modalActions={[
        {
          content: getLocalizedText(
            "property.application.not_eligible.info_modal.cta.continue_without_email",
          ),
          onClick: () => {
            handleChange(statusChanges);
            setCustomFeedbackParams(undefined);
          },
          appearance: "outline",
        },
      ]}
      modalProps={{
        onClose: () => setCustomFeedbackParams(undefined),
        width: "medium",
        children: (
          <Box mb={ESpacings.loose}>
            {getLocalizedText(
              "property.application.not_eligible.info_modal.info",
            )}
          </Box>
        ),
      }}
      isLoading={isLoadingInvite}
    />
  );
};
